import { COURSE_DURATIONS } from '@/config/courseDurations';
import { NAPLAN_STATES, SHARED_NAPLAN_PATH } from '@/config/naplanStates';
import {
  COURSE_FAMILY_STATES,
  familyStatesExcluding,
} from '@/config/courseStateMapping';

/**
 * Canonical course catalogue for the /programs/:state page.
 *
 * This is the single source of truth for displayable course content
 * (names, descriptions, features, year ranges, etc.) shown on the
 * Programs/Courses page. Per-session price numbers are still resolved
 * at runtime via `useCoursePricing(state, pricingCourseType)`.
 *
 * NOTE: this file is intentionally presentation-data only. It does not
 * change schemas, calendars, or pricing logic.
 */

export type AustralianState = 'NSW' | 'VIC' | 'QLD' | 'WA' | 'SA' | 'NT' | 'TAS' | 'ACT';

export type CourseBadge = 'most_popular' | 'new' | 'high_value' | 'popular';

export type CourseDefinition = {
  id: string;
  name: string;
  subtitle?: string;
  description: string;
  features: string[];
  yearRange: string;
  ageRange?: string;
  classSize: string;
  duration: string;
  badges?: CourseBadge[];
  /** Lucide icon name used by the page renderer. */
  iconName:
    | 'GraduationCap'
    | 'Target'
    | 'BookOpen'
    | 'Zap'
    | 'Code'
    | 'Trophy'
    | 'Brain'
    | 'Clock'
    | 'ClipboardCheck';
  /** Tailwind gradient classes used by the existing card design. */
  color: string;
  bgColor: string;
  /** Slug used in `course_pricing.course_type` for live pricing lookup. */
  pricingCourseType: string;
  /** Fallback price string shown when no pricing rows exist for the state. */
  fallbackPrice: string;
  availableIn: AustralianState[];
  href: string;
  /** Homepage grouping. Omit for courses that are not featured there. */
  homepageCategory?: 'selective' | 'foundations' | 'naplan' | 'gate';
  /**
   * Mark as the recommended/default programme within an aspiration reveal.
   * Renders with a stronger accent border. Foundations is primary in its
   * own aspiration; others are off by default.
   */
  primary?: boolean;
  /**
   * Optional "Also available: test pack" sub-section, rendered inside the
   * parent course card. Used for NSW OC TMRW™ → OCPTC and Selective
   * TMRW™ → SPTC. Price is fetched via the same useCoursePricing hook.
   */
  testPack?: {
    name: string;
    description: string;
    pricingCourseType: string;
  };
};


export const COURSES: CourseDefinition[] = [
  // ---------------------------------------------------------------------------
  // Foundations - merged from NSW (`foundations`) and VIC (`foundations-vic`).
  // Both states share the same `course_pricing.course_type = 'foundations'`
  // slug. NSW copy was more detailed (Australian Curriculum specifics, learning
  // augmentation videos, exclusive booklets) so it is retained as the canonical
  // description. yearRange normalised to "Kindergarten, Prep or Pre-primary - Year 8" (covers both
  // VIC's "Prep - Year 6" and NSW's existing range).
  // ---------------------------------------------------------------------------
  {
    id: 'foundations',
    name: 'Foundations Course',
    subtitle: 'Building Strong Academic Foundations',
    description:
      'Comprehensive support for primary and early-secondary students to develop essential literacy and numeracy skills with personalised attention.',
    features: [
      'English 1 Hour weekly (Australian Curriculum- Grammar, Reading, Writing Skills & Phonics)',
      'Maths 1 hour weekly (Australian Curriculum- Core Mathematics, Challenging Students, Learning by Doing)',
      'Learning Augmentation Videos',
      'Exclusive Booklets in Colour',
    ],
    yearRange: 'Kindergarten, Prep or Pre-primary - Year 8',
    ageRange: 'Kindergarten, Prep or Pre-primary - Year 8',
    classSize: '8-10 students',
    duration: COURSE_DURATIONS.foundations.card,
    iconName: 'GraduationCap',
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50',
    pricingCourseType: 'foundations',
    fallbackPrice: 'From $35/session',
    availableIn: ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'NT', 'TAS', 'ACT'],
    href: '/programs/foundations',
    homepageCategory: 'foundations',
    primary: true,

  },

  // ---------------------------------------------------------------------------
  // NSW-only courses
  // ---------------------------------------------------------------------------
  {
    id: 'oc-tmrw',
    name: 'OC TMRW™ Course',
    subtitle: 'Expert OC Preparation with TMRW™ Methodology',
    description:
      'Comprehensive preparation for Opportunity Class entrance with our proven TMRW™ strategies and innovative teaching techniques.',
    features: [
      'T- Thinking Skills 30 mins',
      'M- Mathematical Reasoning 30 mins',
      'R-Reading 30 mins',
      'W- Writing 30 mins',
      'End of Term Exam',
      'Focus of the Week- Strategies',
      'Engineered based on Evidence based- High Impact Teaching Strategies',
    ],
    yearRange: 'Year 3,4 OC',
    ageRange: 'Year 3,4 OC',
    classSize: '8-10 students',
    duration: COURSE_DURATIONS.ocTmrw.card,
    badges: ['most_popular'],
    iconName: 'Target',
    color: 'from-purple-500 to-violet-500',
    bgColor: 'bg-purple-50',
    pricingCourseType: 'oc',
    fallbackPrice: '$77/session',
    availableIn: COURSE_FAMILY_STATES.oc,
    href: '/programs/oc-preparation',
    homepageCategory: 'selective',
    testPack: {
      name: 'OCPTC',
      description: 'Year 4 students closer to the test date. Concentrated practice tests + walkthroughs.',
      pricingCourseType: 'ocptc',
    },
  },

  {
    id: 'selective-tmrw',
    name: 'Selective TMRW™ Course',
    subtitle: 'Selective School Excellence with TMRW™',
    description:
      'Intensive preparation for Selective School entrance examinations with our proven TMRW™ methodology and success strategies.',
    features: [
      'Thinking & Mathematics 2 hours',
      'Reading & Writing 2 hours',
      'End of Term Exam',
      'Focus of the Week- Strategies',
      'Engineered based on Evidence based- High Impact Teaching Strategies',
      'Runs across four terms',
    ],
    yearRange: 'Year 5 - Year 6',
    ageRange: 'Year 5 - Year 6',
    classSize: '8-10 students',
    duration: COURSE_DURATIONS.selectiveTmrw.card,
    badges: ['popular'],
    iconName: 'BookOpen',
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-50',
    pricingCourseType: 'selective',
    fallbackPrice: '$77/session',
    availableIn: COURSE_FAMILY_STATES.selective_nsw,
    href: '/programs/selective-school',
    homepageCategory: 'selective',
    testPack: {
      name: 'SPTC',
      description: 'Year 6 students closer to the test date. Concentrated practice tests + walkthroughs.',
      pricingCourseType: 'sptc',
    },
  },

  {
    id: 'tmrw-leap',
    name: 'OC to Selective Bridging Course',
    subtitle: 'Advanced Academic Acceleration',
    description:
      'Accelerated learning course designed to help high-achieving students leap ahead in their academic journey with innovative methodologies.',
    features: [
      'Advanced Problem Solving',
      'Creative Thinking Development',
      'Leadership Skills Training',
      'Project-Based Learning',
    ],
    yearRange: 'Year 4 - Year 8',
    ageRange: 'Year 4 - Year 8',
    classSize: '8-10 students',
    duration: '2.5 hours per session',
    iconName: 'Zap',
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-50',
    pricingCourseType: 'bridging',
    fallbackPrice: '$77/session',
    availableIn: COURSE_FAMILY_STATES.oc,
    href: '/programs/tmrw-leap',
    homepageCategory: 'selective',
  },


  // ---------------------------------------------------------------------------
  // VIC-only courses
  // ---------------------------------------------------------------------------
  {
    id: 'scholarship-exam',
    name: 'Scholarship Exam Course (SEC)',
    subtitle: 'Victoria Scholarship Success',
    description:
      'Expert preparation for Victoria scholarship exams with proven methodology and specialised coaching for private school entry.',
    features: [
      'Week 1 Humanities/Reading Comprehension+ Mathematics Achievement exams+ Explanations',
      'Week 2- Verbal+ Numerical Reasoning exams + explanations',
      'Week 3 written expression 1 +2 exams + explanations',
    ],
    yearRange: 'Year 5 - Year 6',
    ageRange: 'Year 5 - Year 6',
    classSize: '8-10 students',
    duration: '3 hours per session',
    badges: ['most_popular'],
    iconName: 'Trophy',
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-50',
    pricingCourseType: 'scholarship_exam',
    fallbackPrice: 'From $77 AUD/session',
    availableIn: COURSE_FAMILY_STATES.scholarship_vic,
    href: '/programs/scholarship-exam-course',
    homepageCategory: 'selective',
  },
  {
    id: 'vsec',
    name: 'Victorian Selective Entry Course (VSEC)',
    subtitle: 'Selective High School Entry Preparation',
    description:
      'Comprehensive preparation for the Victorian Selective Entry High School examination with expert coaching and proven strategies.',
    features: [
      'Verbal & Numerical Reasoning Practice',
      'Reading Comprehension & Written Expression',
      'Mathematics Achievement Preparation',
      'Full-length Practice Exams with Feedback',
    ],
    yearRange: 'Year 8 Entry (Year 7 Students)',
    ageRange: 'Year 8 Entry (Year 7 Students)',
    classSize: '8-10 students',
    duration: COURSE_DURATIONS.vsec.card,
    badges: ['new'],
    iconName: 'Target',
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-50',
    pricingCourseType: 'vsec',
    fallbackPrice: 'From $77 AUD/session',
    availableIn: COURSE_FAMILY_STATES.selective_vic,
    href: '/programs/selective-school',
    homepageCategory: 'selective',
  },
  {
    id: 'afterschool-vic',
    name: 'After School Course',
    subtitle: 'Comprehensive After School Support',
    description:
      'Complete after-school support combining homework assistance, skill development, and recreational activities.',
    features: [
      'Homework Support & Assistance',
      'Academic Skill Development',
      'Recreation & Social Activities',
      'Safe Learning Environment',
    ],
    yearRange: 'Prep - Year 6',
    ageRange: 'Prep - Year 6',
    classSize: '12-15 students',
    duration: '2.5 hours per session',
    iconName: 'Clock',
    color: 'from-cyan-500 to-blue-500',
    bgColor: 'bg-cyan-50',
    pricingCourseType: 'afterschool',
    fallbackPrice: 'From $55 AUD/session',
    availableIn: ['VIC'],
    href: '/programs/afterschool',
  },

  // ---------------------------------------------------------------------------
  // NOTE: The standalone `year-7-8-foundations` entry was removed because the
  // Foundations Course now covers Kindergarten, Prep or Pre-primary - Year 8 across all states.
  // The dedicated /programs/year-7-8-foundations route (Year78Foundations.tsx)
  // is intentionally left in place and out of scope for this change.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // NAPLAN Preparation - one offer for every state, built from the same card
  // content. Each state entry links to its own NAPLAN template route; prices
  // come from the state's course_pricing 'naplan_writing_numeracy' row.
  // ---------------------------------------------------------------------------
  ...([
    { id: 'naplan-nsw', availableIn: ['NSW'], href: NAPLAN_STATES.NSW.path },
    { id: 'naplan-vic', availableIn: ['VIC'], href: NAPLAN_STATES.VIC.path },
    { id: 'naplan-wa', availableIn: ['WA'], href: NAPLAN_STATES.WA.path },
    { id: 'naplan-other', availableIn: familyStatesExcluding('naplan', ['NSW', 'VIC', 'WA']), href: SHARED_NAPLAN_PATH },
  ] as const).map(({ id, availableIn, href }): CourseDefinition => ({
    id,
    name: 'NAPLAN Preparation',
    subtitle: 'Targeted preparation for students sitting Years 3, 5 and 7 NAPLAN',
    description:
      'One weekly session alternating Writing and Numeracy with Reading and Conventions, covering every NAPLAN test area before the March test window.',
    features: [
      'Writing and Numeracy on odd weeks',
      'Reading and Conventions on even weeks',
      'Timed test practice and detailed progress tracking',
      'Written reports at Week 5 and Week 10',
    ],
    yearRange: 'Years 3, 5 and 7',
    ageRange: 'Years 3, 5 and 7',
    classSize: '8-10 students',
    duration: COURSE_DURATIONS.naplan.card,
    iconName: 'ClipboardCheck',
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50',
    pricingCourseType: 'naplan_writing_numeracy',
    fallbackPrice: 'Contact your local centre',
    availableIn: [...availableIn],
    href,
    homepageCategory: 'naplan',
  })),
  // ---------------------------------------------------------------------------
  // ASET / GATE Test Preparation - Western Australia only. State visibility is
  // taken from COURSE_FAMILY_STATES.gate so it can never diverge from the
  // shared course-to-state mapping. Content sourced from
  // src/pages/programs/AsetGateHub.tsx.
  // ---------------------------------------------------------------------------
  {
    id: 'aset-gate',
    name: 'ASET / GATE Test Preparation',
    subtitle: 'Year 6 students sitting for Year 7 entry',
    description:
      'Preparation for the WA Academic Selective Entrance Test (ASET), the test that selects for Gifted and Talented Education (GATE) placement. Covers reading comprehension, communicating ideas in writing, quantitative reasoning and abstract reasoning.',
    features: [
      'Reading comprehension - inference and interpretation under time pressure',
      'Communicating ideas in writing with teacher-marked feedback',
      'Quantitative reasoning - number, pattern, ratio and measurement, no calculator',
      'Abstract reasoning - visual pattern logic and pacing strategies',
      'Test technique and timing for a single-sitting exam',
    ],
    yearRange: 'Year 6',
    ageRange: 'Year 6',
    classSize: '8-10 students',
    duration: COURSE_DURATIONS.gateTmrw.card,
    iconName: 'Trophy',
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-purple-50',
    pricingCourseType: 'gate',
    fallbackPrice: 'Contact your local centre',
    availableIn: COURSE_FAMILY_STATES.gate,
    href: '/courses/aset-gate-test-preparation',
    homepageCategory: 'gate',
  },
];


export const SUPPORTED_PROGRAM_STATES: AustralianState[] = ['NSW', 'VIC', 'WA'];

export const STATE_FULL_NAME: Record<AustralianState, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  NT: 'Northern Territory',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
};

export const coursesForState = (state: AustralianState): CourseDefinition[] =>
  COURSES.filter((c) => c.availableIn.includes(state));

export const isSupportedProgramState = (
  value: string | undefined | null,
): value is AustralianState =>
  !!value && (SUPPORTED_PROGRAM_STATES as string[]).includes(value.toUpperCase());

export const PROGRAMS_DEFAULT_STATE: AustralianState = 'NSW';
